import { Admin, Resource, CustomRoutes, Loading } from 'react-admin'
import { Route } from 'react-router-dom'
import { lazy, StrictMode, Suspense } from 'react'

import { appTheme } from '@/Layout/theme'
import { AdminLayout } from '@/Layout/AdminLayout'
import { Dashboard } from './Dashboard'
import { EResource } from './types'
import { CUSTOMER_CARE_ENABLED } from '@/consts/constants'
import {
  RESET_PASSWORD,
  SETTINGS,
  VARIANTS_MULTI_UPLOADS,
  VOUCHER_STOCK_UPLOAD_DETAILS,
  PARSED_VOUCHER_UPLOAD,
  CUSTOMER_CARE_BULK_MERGE,
  CUSTOMER_CARE_IDENTITY_CORRECTION,
  CUSTOMER_CARE_MERGE_ALL,
  DEEP_LINK_GENERATOR,
} from '@/services/api/routes'
import { dataProvider } from './dataProvider'

// Lazy load all resources - this splits them into separate chunks
const ProductsList = lazy(() => import('@/pages/Products').then((m) => ({ default: m.ProductsList })))
const VariantsList = lazy(() => import('@/pages/Variants').then((m) => ({ default: m.VariantsList })))
const VariantCostsList = lazy(() => import('@/pages/VariantCosts').then((m) => ({ default: m.VariantCostsList })))
const BrandsList = lazy(() => import('@/pages/Brands').then((m) => ({ default: m.BrandsList })))
const MainCategoriesList = lazy(() => import('@/pages/MainCategories').then((m) => ({ default: m.MainCategoriesList })))
const SubCategoriesList = lazy(() => import('@/pages/SubCategories').then((m) => ({ default: m.SubCategoriesList })))
const OrderShow = lazy(() => import('@/pages/Order').then((m) => ({ default: m.OrderShow })))
const OrdersList = lazy(() => import('@/pages/Orders').then((m) => ({ default: m.OrdersList })))
const PromocodesList = lazy(() => import('@/pages/Promocodes').then((m) => ({ default: m.PromocodesList })))
const MerchantsOrdersList = lazy(() =>
  import('@/pages/MerchantsOrders').then((m) => ({ default: m.MerchantsOrdersList })),
)
const SuppliersOrdersList = lazy(() =>
  import('@/pages/SuppliersOrders').then((m) => ({ default: m.SuppliersOrdersList })),
)
const SupplierMappingsList = lazy(() =>
  import('@/pages/SupplierMappings').then((m) => ({ default: m.SupplierMappingsList })),
)
const SuppliersList = lazy(() => import('@/pages/Suppliers').then((m) => ({ default: m.SuppliersList })))
const SupplierShow = lazy(() => import('@/modules/Suppliers/SupplierShow').then((m) => ({ default: m.SupplierShow })))
const RolesAndPermissionsManagementList = lazy(() =>
  import('@/pages/RolesAndPermissionsManagement').then((m) => ({ default: m.RolesAndPermissionsManagementList })),
)
const UsersList = lazy(() => import('@/pages/Users').then((m) => ({ default: m.UsersList })))
const AdminsList = lazy(() => import('@/pages/Admins').then((m) => ({ default: m.AdminsList })))
const Settings = lazy(() => import('@/pages/Settings').then((m) => ({ default: m.Settings })))
const MarketplaceVariantsList = lazy(() =>
  import('@/pages/MarketplaceVariants').then((m) => ({ default: m.MarketplaceVariantsList })),
)
const ContentsList = lazy(() => import('@/pages/Contents').then((m) => ({ default: m.ContentsList })))
const CatalogsList = lazy(() => import('@/pages/Catalog').then((m) => ({ default: m.CatalogsList })))
const OfferList = lazy(() => import('@/pages/Offers').then((m) => ({ default: m.OfferList })))
const VoucherBatchesList = lazy(() => import('@/pages/VoucherBatches').then((m) => ({ default: m.VoucherBatchesList })))
const BulkPromoCodesList = lazy(() => import('@/pages/BulkPromoCodes').then((m) => ({ default: m.BulkPromoCodesList })))
const BulkPromoCodesShow = lazy(() => import('@/pages/BulkPromoCodes').then((m) => ({ default: m.BulkPromoCodesShow })))
const PromoRulesList = lazy(() => import('@/pages/PromoRules').then((m) => ({ default: m.PromoRulesList })))
const PromoRulesShow = lazy(() => import('@/pages/PromoRules').then((m) => ({ default: m.PromoRulesShow })))
const CampaignsList = lazy(() => import('@/pages/Campaigns').then((m) => ({ default: m.CampaignsList })))

// Create/Edit components
const PromocodesCreate = lazy(() =>
  import('@/modules/Promocodes/PromocodesCreate').then((m) => ({ default: m.PromocodesCreate })),
)
const PromocodesEdit = lazy(() =>
  import('@/modules/Promocodes/PromocodesEdit').then((m) => ({ default: m.PromocodesEdit })),
)
const BrandsCreate = lazy(() => import('@/modules/Brands/BrandsCreate').then((m) => ({ default: m.BrandsCreate })))
const BrandsEdit = lazy(() => import('@/modules/Brands/BrandsEdit').then((m) => ({ default: m.BrandsEdit })))
const ProductsCreate = lazy(() =>
  import('@/modules/Products/ProductsCreate').then((m) => ({ default: m.ProductsCreate })),
)
const ProductsEdit = lazy(() => import('@/modules/Products/ProductsEdit').then((m) => ({ default: m.ProductsEdit })))
const MainCategoriesCreate = lazy(() =>
  import('@/modules/MainCategories/MainCategoriesCreate').then((m) => ({ default: m.MainCategoriesCreate })),
)
const MainCategoriesEdit = lazy(() =>
  import('@/modules/MainCategories/MainCategoriesEdit').then((m) => ({ default: m.MainCategoriesEdit })),
)
const SubCategoriesCreate = lazy(() =>
  import('@/modules/SubCategories/SubCategoriesCreate').then((m) => ({ default: m.SubCategoriesCreate })),
)
const SubCategoriesEdit = lazy(() =>
  import('@/modules/SubCategories/SubCategoriesEdit').then((m) => ({ default: m.SubCategoriesEdit })),
)
const AdminsCreate = lazy(() => import('@/modules/Admins/AdminsCreate').then((m) => ({ default: m.AdminsCreate })))
const AdminsEdit = lazy(() => import('@/modules/Admins/AdminsEdit').then((m) => ({ default: m.AdminsEdit })))
const VariantCostsCreate = lazy(() =>
  import('@/modules/VariantCosts/VariantCostsCreate').then((m) => ({ default: m.VariantCostsCreate })),
)
const VariantCostsShow = lazy(() =>
  import('@/modules/VariantCosts/VariantCostsShow').then((m) => ({ default: m.VariantCostsShow })),
)
const VariantsCreate = lazy(() =>
  import('@/modules/Variants/VariantsCreate').then((m) => ({ default: m.VariantsCreate })),
)
const VariantsEdit = lazy(() => import('@/modules/Variants/VariantsEdit').then((m) => ({ default: m.VariantsEdit })))
const MarketplaceCreate = lazy(() =>
  import('@/modules/Marketplace/MarketplaceCreate').then((m) => ({ default: m.MarketplaceCreate })),
)
const MarketplaceEdit = lazy(() =>
  import('@/modules/Marketplace/MarketplaceEdit').then((m) => ({ default: m.MarketplaceEdit })),
)
const ContentCreate = lazy(() =>
  import('@/modules/Contents/ContentsCreate').then((m) => ({ default: m.ContentCreate })),
)
const ContentEdit = lazy(() => import('@/modules/Contents/ContentsEdit').then((m) => ({ default: m.ContentEdit })))
const CatalogsCreate = lazy(() =>
  import('@/modules/Catalog/CatalogsCreate').then((m) => ({ default: m.CatalogsCreate })),
)
const CatalogEdit = lazy(() => import('@/modules/Catalog/CatalogsEdit').then((m) => ({ default: m.CatalogEdit })))
const OfferEdit = lazy(() => import('@/modules/Offers/OffersEdit').then((m) => ({ default: m.OfferEdit })))
const OfferCreate = lazy(() => import('@/modules/Offers/OffersCreate').then((m) => ({ default: m.OfferCreate })))
const VoucherBatchesEdit = lazy(() =>
  import('@/modules/VoucherBatches/VoucherBatchesEdit').then((m) => ({ default: m.VoucherBatchesEdit })),
)
const BulkPromoCodesCreate = lazy(() =>
  import('@/modules/BulkPromoCodes/BulkPromoCodesCreate').then((m) => ({ default: m.BulkPromoCodesCreate })),
)
const BulkPromoCodesEdit = lazy(() =>
  import('@/modules/BulkPromoCodes/BulkPromoCodesEdit').then((m) => ({ default: m.BulkPromoCodesEdit })),
)
const PromoRulesCreate = lazy(() =>
  import('@/modules/PromoRules/PromoRulesCreate').then((m) => ({ default: m.PromoRulesCreate })),
)
const PromoRulesEdit = lazy(() =>
  import('@/modules/PromoRules/PromoRulesEdit').then((m) => ({ default: m.PromoRulesEdit })),
)
const SupplierMappingsCreate = lazy(() =>
  import('@/modules/SupplierMappings/SupplierMappingsCreate').then((m) => ({ default: m.SupplierMappingsCreate })),
)
const SupplierMappingsShow = lazy(() =>
  import('@/modules/SupplierMappings/SupplierMappingsShow').then((m) => ({ default: m.SupplierMappingsShow })),
)
const SupplierMappingsEdit = lazy(() =>
  import('@/modules/SupplierMappings/SupplierMappingsEdit').then((m) => ({ default: m.SupplierMappingsEdit })),
)
const CampaignsCreate = lazy(() =>
  import('@/modules/Campaigns/CampaignsCreate').then((m) => ({ default: m.CampaignsCreate })),
)
const CampaignsEdit = lazy(() =>
  import('@/modules/Campaigns/CampaignsEdit').then((m) => ({ default: m.CampaignsEdit })),
)

// Customer Care
const SuperQiIdentityCorrection = lazy(() =>
  import('@/modules/CustomerCare/SuperQiIdentityCorrection').then((m) => ({
    default: m.SuperQiIdentityCorrection,
  })),
)
const SuperQiBulkMerge = lazy(() =>
  import('@/modules/CustomerCare/SuperQiBulkMerge').then((m) => ({
    default: m.SuperQiBulkMerge,
  })),
)
const SuperQiMergeAll = lazy(() =>
  import('@/modules/CustomerCare/SuperQiMergeAll').then((m) => ({
    default: m.SuperQiMergeAll,
  })),
)

// Custom routes/components
const AllowedPages = lazy(() => import('@/components/AllowedPages/AllowedPages'))
const VariantsMultiUpload = lazy(() => import('@/modules/Variants/VariantsMultiUpload'))
const VoucherStockUploadDetails = lazy(() => import('@/modules/VoucherBatches/VoucherStockUploadDetails'))
const VoucherStockUpload = lazy(() => import('@/modules/VoucherBatches/VoucherStockUpload'))
const ParsedVoucherUpload = lazy(() => import('@/modules/VoucherBatches/ParsedVoucherUpload'))
const DeepLinkGenerator = lazy(() => import('@/modules/DeepLinks/DeepLinkGenerator'))

const App = () => {
  return (
    <StrictMode>
      <Suspense fallback={<Loading />}>
        <Admin
          title="Digital Zone Admin"
          theme={appTheme}
          dataProvider={dataProvider}
          layout={AdminLayout}
          dashboard={Dashboard}
        >
          <Resource name={EResource.Variants} list={VariantsList} create={VariantsCreate} edit={VariantsEdit} />
          <Resource
            name={EResource.MarketplaceVariants}
            list={MarketplaceVariantsList}
            create={MarketplaceCreate}
            edit={MarketplaceEdit}
          />
          <Resource
            name={EResource.CommunityCampaigns}
            list={CampaignsList}
            create={CampaignsCreate}
            edit={CampaignsEdit}
          />
          <Resource name={EResource.Contents} list={ContentsList} create={ContentCreate} edit={ContentEdit} />
          <Resource
            name={EResource.VoucherBatches}
            list={VoucherBatchesList}
            edit={VoucherBatchesEdit}
            create={VoucherStockUpload}
          />
          <Resource name={EResource.Catalogs} list={CatalogsList} create={CatalogsCreate} edit={CatalogEdit} />
          <Resource name={EResource.Offers} list={OfferList} edit={OfferEdit} create={OfferCreate} />
          <Resource
            name={EResource.VariantCosts}
            list={VariantCostsList}
            create={VariantCostsCreate}
            show={VariantCostsShow}
          />
          <Resource name={EResource.Products} list={ProductsList} create={ProductsCreate} edit={ProductsEdit} />
          <Resource name={EResource.Brands} list={BrandsList} create={BrandsCreate} edit={BrandsEdit} />
          <Resource
            name={EResource.MainCategories}
            list={MainCategoriesList}
            create={MainCategoriesCreate}
            edit={MainCategoriesEdit}
          />
          <Resource
            name={EResource.SubCategories}
            list={SubCategoriesList}
            create={SubCategoriesCreate}
            edit={SubCategoriesEdit}
          />
          <Resource name={EResource.Orders} list={OrdersList} show={OrderShow} />
          <Resource name={EResource.Promocodes} list={PromocodesList} create={PromocodesCreate} edit={PromocodesEdit} />
          <Resource
            name={EResource.BulkPromoCodes}
            list={BulkPromoCodesList}
            create={BulkPromoCodesCreate}
            edit={BulkPromoCodesEdit}
            show={BulkPromoCodesShow}
          />
          <Resource
            name={EResource.PromoRules}
            list={PromoRulesList}
            create={PromoRulesCreate}
            edit={PromoRulesEdit}
            show={PromoRulesShow}
          />
          <Resource name={EResource.Users} list={UsersList} />
          <Resource name={EResource.RolesAndPermissions} list={RolesAndPermissionsManagementList} />
          <CustomRoutes>
            <Route path={SETTINGS()} element={<Settings />} />
          </CustomRoutes>
          <Resource name={EResource.Admins} list={AdminsList} edit={AdminsEdit} create={AdminsCreate} />
          <Resource name={EResource.MerchantsOrders} list={MerchantsOrdersList} />
          <Resource name={EResource.SuppliersOrders} list={SuppliersOrdersList} />
          <Resource name={EResource.Suppliers} list={SuppliersList} show={SupplierShow} />
          <Resource
            name={EResource.SupplierMappings}
            list={SupplierMappingsList}
            show={SupplierMappingsShow}
            edit={SupplierMappingsEdit}
            create={SupplierMappingsCreate}
          />
          <Resource name={EResource.AllowedPages} list={AllowedPages} />
          <CustomRoutes noLayout>
            <Route path={RESET_PASSWORD()} element={<RESET_PASSWORD />} />
          </CustomRoutes>
          {CUSTOMER_CARE_ENABLED && (
            <CustomRoutes>
              <Route path={CUSTOMER_CARE_IDENTITY_CORRECTION()} element={<SuperQiIdentityCorrection />} />
              <Route path={CUSTOMER_CARE_BULK_MERGE()} element={<SuperQiBulkMerge />} />
              <Route path={CUSTOMER_CARE_MERGE_ALL()} element={<SuperQiMergeAll />} />
            </CustomRoutes>
          )}
          <CustomRoutes>
            <Route path={VARIANTS_MULTI_UPLOADS()} element={<VariantsMultiUpload />} />
            <Route path={PARSED_VOUCHER_UPLOAD()} element={<ParsedVoucherUpload />} />
          </CustomRoutes>
          <CustomRoutes>
            <Route path={VOUCHER_STOCK_UPLOAD_DETAILS()} element={<VoucherStockUploadDetails />} />
          </CustomRoutes>
          <CustomRoutes>
            <Route path={DEEP_LINK_GENERATOR()} element={<DeepLinkGenerator />} />
          </CustomRoutes>
        </Admin>
      </Suspense>
    </StrictMode>
  )
}

export default App
